import type { ChangelogEntry, SourceRef } from '../../data/pseo/types';

interface Props {
  lastVerified: string;
  sources: SourceRef[];
  changelog?: ChangelogEntry[];
  notes?: string[];
  /** "Verified" for sourced specs; "Updated" for original content. */
  label?: string;
}

/** ISO date → "Oct 16, 2026", fixed to UTC so server and client render the same day. */
export const formatIsoDate = (isoDate: string) =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-US', { dateStyle: 'medium', timeZone: 'UTC' });

/** "Verified {date} against {source}" plus source links, notes and a collapsible changelog. */
export function LastVerifiedBadge({ lastVerified, sources, changelog = [], notes = [], label = 'Verified' }: Props) {
  const [primary] = sources;

  return (
    <div className="rounded-xl border border-foreground-200 bg-content1/40 p-4 text-sm text-foreground-600">
      <p className="flex flex-wrap items-center gap-x-1">
        <span className="inline-block h-2 w-2 rounded-full bg-success" aria-hidden />
        <span className="font-semibold text-foreground-700">
          {label} {formatIsoDate(lastVerified)}
        </span>
        {primary ? (
          <>
            against{' '}
            <a
              href={primary.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2"
            >
              {primary.label}
            </a>
          </>
        ) : null}
      </p>
      {sources.length > 1 ? (
        <ul className="mt-2 list-disc pl-5">
          {sources.slice(1).map((source) => (
            <li key={source.url}>
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                {source.label}
              </a>
            </li>
          ))}
        </ul>
      ) : null}
      {notes.length > 0 ? (
        <ul className="mt-2 list-disc pl-5 text-foreground-500">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
      {changelog.length > 0 ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-foreground-500">Changelog</summary>
          <ul className="mt-1 list-disc pl-5 text-foreground-500">
            {changelog.map((entry) => (
              <li key={`${entry.date}-${entry.change}`}>
                <time dateTime={entry.date}>{entry.date}</time>: {entry.change}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
